"use client";

import Link from "next/link";
import { PillButton } from "@/components/ui/pill-button";
import { MuxVideo } from "@/components/ui/mux-video";
import type { VideoAssetKey } from "@/lib/video-assets";

export interface ShowcaseCardProps {
  num: string;
  title: string;
  desc: string;
  video?: string;
  videoAssetKey?: VideoAssetKey;
  img?: string;
  blogHref?: string;
  githubLinks?: { label: string; href: string }[];
}

export function ShowcaseCard({
  num,
  title,
  desc,
  video,
  videoAssetKey,
  img,
  blogHref,
  githubLinks,
}: ShowcaseCardProps) {
  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-white/[0.06] bg-[#09090a]">
        {videoAssetKey ? (
          <MuxVideo
            assetKey={videoAssetKey}
            className="aspect-video w-full object-cover"
          />
        ) : video ? (
          <video
            src={video}
            autoPlay
            loop
            muted
            playsInline
            className="aspect-video w-full object-cover"
          />
        ) : img ? (
          <img
            src={img}
            alt={title}
            className="w-full block brightness-[0.85] object-cover"
          />
        ) : (
          <div className="aspect-video w-full" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col min-[700px]:flex-row min-[700px]:items-center gap-2 min-[700px]:gap-3">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-mono text-white/30">{num}</span>
            <h3 className="text-lg font-normal tracking-tight">{title}</h3>
          </div>
          {(blogHref || githubLinks) && (
            <div className="flex gap-2">
              {blogHref && (
                <Link href={blogHref}>
                  <PillButton
                    variant="secondary"
                    icon="bookmark"
                    className="text-[11px] px-3 py-1"
                  >
                    Blog
                  </PillButton>
                </Link>
              )}
              {githubLinks?.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PillButton
                    variant="secondary"
                    icon="github"
                    className="text-[11px] px-3 py-1"
                  >
                    {link.label}
                  </PillButton>
                </a>
              ))}
            </div>
          )}
        </div>
        <p className="text-sm font-light text-white/40 leading-relaxed">
          {desc}
        </p>
      </div>
    </div>
  );
}
